import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft, Mail } from "lucide-react";

export default function Terms() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-10" data-testid="terms-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
      </Link>

      <div className="mt-6 flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
          <ScrollText className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-primary">Légal</p>
          <h1 className="font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">
            Conditions générales d'utilisation
          </h1>
        </div>
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })} · Version 2026.02
      </p>

      <article className="prose prose-neutral mt-8 max-w-none space-y-6 text-sm leading-relaxed text-foreground/90">
        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">1. Objet</h2>
          <p>
            Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'usage de la plateforme
            <strong> Saor</strong>, service de mise en relation entre chauffeurs privés VTC et deux catégories de
            demandeurs : d'une part des <strong>établissements hôteliers</strong> et <strong>dispatchers</strong>
            professionnels, d'autre part des <strong>clients particuliers</strong> réservant pour eux-mêmes.
            La plateforme est éditée par Saor (situé à Nice, France) et distribuée sous la forme d'un site web
            responsive (saor.online), d'une application <strong>iOS</strong> disponible sur l'App Store, et d'une
            application <strong>Android</strong> disponible sur Google Play. Toute utilisation de la plateforme
            implique l'acceptation pleine et entière des présentes CGU.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">2. Définitions</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li><strong>Saor</strong> : plateforme web / PWA / applications iOS &amp; Android, et son éditeur.</li>
            <li><strong>Hôtel</strong> : établissement hôtelier inscrit qui réserve des courses pour ses clients (canal B2B).</li>
            <li><strong>Dispatch</strong> : partenaire (agence, conciergerie, dispatcheur) qui apporte des courses sans être lui-même un hôtel.</li>
            <li><strong>Client B2C</strong> : particulier disposant d'un compte Saor et réservant une course pour lui-même via l'application mobile ou le site.</li>
            <li><strong>Chauffeur</strong> : professionnel VTC validé par Saor qui accepte des courses.</li>
            <li><strong>Client final</strong> : passager transporté, qu'il ait été enregistré par un hôtel, un dispatch ou par lui-même en B2C.</li>
            <li><strong>Course</strong> : prestation de transport entre une adresse de départ et une adresse d'arrivée.</li>
            <li><strong>Trajet forfaitaire</strong> : prix fixe défini par l'administrateur Saor entre deux adresses précises pour un lien hôtel ou promotionnel donné.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">3. Inscription et validation des comptes</h2>
          <p>
            L'inscription est gratuite. Les hôtels créent un compte avec leurs informations professionnelles.
            Les chauffeurs doivent fournir l'intégralité des documents légaux requis (carte VTC, KBIS/SIRENE, RIB,
            attestations d'assurance, certificat d'inscription au registre VTC, permis, identité, photos véhicule).
            Saor valide manuellement chaque dossier sous 48 heures ouvrées. Saor se réserve le droit de refuser
            ou suspendre tout compte en cas de dossier incomplet, frauduleux ou non conforme.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">4. Rôle de Saor</h2>
          <p>
            Saor agit en qualité <strong>d'intermédiaire technique</strong>. La plateforme ne fournit pas elle-même
            de service de transport : elle facilite la mise en relation, le dispatch et le suivi entre les
            chauffeurs et leurs clients (qu'ils réservent via un hôtel, un dispatch ou en direct dans l'application
            B2C). Le contrat de transport est conclu directement entre le chauffeur et le client final.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">5. Tarification et commission</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong>Grille B2B (hôtels &amp; dispatchers) :</strong> le tarif est calculé en deux tranches :
              (1) <strong>4 km ou moins</strong> → forfait fixe (Berline 25 €, VAN 35 €, Premium 50 €) ;
              (2) <strong>au-delà de 4 km</strong> → prise en charge longue (Berline 35 €, VAN 50 €, Premium 60 €)
              + 2,50 €/km à partir du 4ᵉ kilomètre.
            </li>
            <li>
              <strong>Grille B2C (clients particuliers via l'application) :</strong> prise en charge de
              <strong> 10 €</strong> + <strong>2,60 €/km</strong>, majoration <strong>+10 €</strong> pour un VAN
              et <strong>+15 €</strong> pour la catégorie Premium.
            </li>
            <li>
              <strong>Trajets forfaitaires :</strong> pour certains liens hôtels ou codes promo, Saor peut définir
              des prix fixes entre deux adresses précises (aéroport, gare, port…). Ces prix priment sur la grille
              kilométrique ci-dessus dans les deux sens du trajet.
            </li>
            <li>
              <strong>Majoration événement :</strong> lors d'événements spécifiques (Grand Prix de Monaco, Festival
              de Cannes, jour férié, forte demande…), Saor peut appliquer une majoration temporaire clairement
              affichée dans le devis avant validation.
            </li>
            <li>
              <strong>Répartition pour les courses apportées par un Hôtel :</strong>
              <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                <li>Saor prélève une commission de <strong>25 %</strong> sur le prix de la course.</li>
                <li>Le chauffeur perçoit <strong>75 %</strong> du prix.</li>
              </ul>
            </li>
            <li>
              <strong>Répartition pour les courses apportées par un Dispatch :</strong>
              <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                <li><strong>20 %</strong> reversés au Dispatch (rétribution de l'apport d'affaires).</li>
                <li><strong>10 %</strong> de commission Saor.</li>
                <li><strong>70 %</strong> versés au chauffeur.</li>
              </ul>
            </li>
            <li>
              <strong>Répartition pour les courses B2C (clients particuliers) :</strong>
              <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                <li>Saor prélève une commission de <strong>25 %</strong> sur le prix de la course.</li>
                <li>Le chauffeur perçoit <strong>75 %</strong> du prix.</li>
              </ul>
            </li>
            <li>
              <strong>Moyens de paiement :</strong>
              <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                <li><strong>Apple Pay / carte bancaire en ligne :</strong> encaissement via <strong>Stripe</strong>
                  au moment de la réservation. Les fonds sont reversés au chauffeur (net de commission) selon les
                  modalités de reversement convenues.</li>
                <li><strong>Square Tap-to-Pay :</strong> paiement à bord par carte / Apple Pay / Google Pay sans
                  contact, encaissé directement par le chauffeur sur son téléphone. Le chauffeur reverse ensuite la
                  commission Saor (et la part Dispatch le cas échéant).</li>
                <li><strong>Espèces :</strong> remises directement au chauffeur, qui reverse la commission Saor selon
                  les modalités convenues.</li>
              </ul>
            </li>
            <li>
              <strong>Programme de parrainage :</strong> les clients B2C peuvent partager un code parrain avec leurs
              proches. Le filleul obtient <strong>10 €</strong> de crédit dès son inscription, et le parrain reçoit
              <strong> 10 €</strong> de crédit dès que le filleul termine sa première course. Les crédits sont
              déduits automatiquement du prochain devis.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">6. Obligations des chauffeurs</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>Respecter strictement la réglementation française du transport public particulier de personnes (VTC).</li>
            <li>Maintenir à jour ses documents légaux (assurances, carte VTC, KBIS).</li>
            <li>Posséder un véhicule conforme aux exigences VTC, propre et en bon état.</li>
            <li>Adopter un comportement professionnel et courtois envers les clients et les hôtels partenaires.</li>
            <li>Respecter les horaires des courses programmées et confirmer/refuser les courses immédiates dans des délais raisonnables.</li>
            <li>Émettre une facture pour chaque course payée, avec son SIRET et son n° TVA si applicable.</li>
            <li>
              <strong>Une seule course active à la fois — règle du chaînage :</strong> un chauffeur ne peut mener
              qu'une seule course en cours (statuts « acceptée », « prise en charge » ou « en trajet ») à la fois.
              Il ne reçoit une nouvelle demande de course pendant qu'il est en course que si le temps total avant
              d'arriver au pickup du client suivant (temps restant sur sa course actuelle + trajet à vide vers le
              nouveau pickup) est <strong>inférieur ou égal à 6 minutes</strong>. Au-delà de ce seuil, la course
              est proposée à un autre chauffeur disponible pour ne pas faire attendre le client.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">7. Obligations des hôtels, dispatches et clients B2C</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>Fournir des informations exactes lors de l'inscription et lors de chaque réservation (adresse, client, téléphone).</li>
            <li>Informer le client final de la transmission de ses données au chauffeur dans le but unique d'effectuer la course.</li>
            <li>Régler ou faire régler les courses selon le mode de paiement choisi (Apple Pay / Stripe, Square Tap-to-Pay ou espèces).</li>
            <li>Ne pas utiliser la plateforme à des fins frauduleuses ou contraires à la loi.</li>
            <li>
              <strong>Notifications push :</strong> l'utilisateur accepte de recevoir des notifications OneSignal
              (iOS / Android / Web) liées à ses courses (chauffeur accepté, arrivée, démarrage, fin de course). Il peut
              les désactiver à tout moment depuis les réglages de son téléphone ou navigateur.
            </li>
            <li>
              <strong>Géolocalisation :</strong> l'application peut demander l'accès à la position GPS pour améliorer
              le suivi en temps réel. L'accès reste optionnel côté client mais est requis côté chauffeur en course.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">8. Annulation</h2>
          <ul className="ml-5 list-disc space-y-1">
            <li>Un hôtel, un dispatch ou un client B2C peut annuler une course tant qu'aucun chauffeur n'a effectué la prise en charge (statut différent de « prise en charge »).</li>
            <li>Le chauffeur peut relâcher une course acceptée tant que le client n'a pas été pris en charge ; la course est alors automatiquement re-dispatchée aux autres chauffeurs éligibles.</li>
            <li>Un chauffeur cumulant plus de 3 annulations sur 7 jours glissants est signalé à l'administrateur et peut être suspendu.</li>
            <li>Un chauffeur en course ne peut pas accepter simultanément plusieurs courses ; il ne peut accepter une nouvelle demande que dans la fenêtre de chaînage définie à l'article 6.</li>
            <li>En cas d'annulation après démarrage d'une course payée par Apple Pay / Stripe, un remboursement partiel ou total peut être décidé par Saor sur la base de la distance déjà parcourue et de la responsabilité de la partie annulatrice.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">9. Suspension &amp; résiliation</h2>
          <p>
            Saor se réserve le droit de suspendre temporairement ou définitivement tout compte en cas de :
            non-respect des présentes CGU, fausse déclaration, fraude, comportement portant atteinte à la
            réputation de la plateforme, ou pour toute raison légitime tenant à la sécurité de la communauté.
            Le chauffeur ou l'hôtel suspendu peut contacter Saor pour contester la décision.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">10. Responsabilité</h2>
          <p>
            Saor s'engage à fournir un service techniquement disponible avec la meilleure diligence. Saor ne saurait
            être tenu responsable des incidents survenant pendant une course (retard, accident, comportement),
            qui relèvent de la responsabilité directe du chauffeur en tant que professionnel VTC autonome
            disposant de ses propres assurances.
          </p>
          <p>
            Saor ne garantit ni un volume minimum de courses pour les chauffeurs, ni la disponibilité immédiate
            d'un chauffeur pour les hôtels.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">11. Propriété intellectuelle</h2>
          <p>
            La marque Saor, le logo, les textes, l'interface et le code source sont la propriété exclusive de Saor.
            Toute reproduction sans autorisation est interdite.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">12. Données personnelles</h2>
          <p>
            Le traitement des données personnelles est détaillé dans notre{" "}
            <Link to="/privacy" className="text-primary underline-offset-2 hover:underline">
              politique de confidentialité
            </Link>
            . Pour toute demande de suppression de votre compte, contactez-nous à{" "}
            <a href="mailto:saor.pro@icloud.com" className="text-primary underline-offset-2 hover:underline">
              saor.pro@icloud.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">13. Modification des CGU</h2>
          <p>
            Saor peut faire évoluer ces CGU. Les utilisateurs seront informés par e-mail des changements
            substantiels. La poursuite de l'utilisation de la plateforme après modification vaut acceptation
            des nouvelles conditions.
          </p>
        </section>

        <section>
          <h2 className="font-[Outfit] text-xl font-medium text-foreground">14. Droit applicable &amp; juridiction</h2>
          <p>
            Les présentes CGU sont soumises au droit français. Tout litige sera, à défaut de résolution amiable,
            porté devant les tribunaux compétents du ressort de Nice.
          </p>
        </section>

        <section className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5">
          <h2 className="flex items-center gap-2 font-[Outfit] text-xl font-medium text-foreground">
            <Mail className="h-5 w-5 text-primary" />
            Contact
          </h2>
          <p className="mt-2">
            Pour toute question, réclamation ou demande de suppression de compte :
          </p>
          <p className="mt-2">
            <a
              href="mailto:saor.pro@icloud.com"
              className="text-base font-medium text-primary underline-offset-2 hover:underline"
              data-testid="terms-contact-email"
            >
              saor.pro@icloud.com
            </a>
          </p>
        </section>
      </article>
    </div>
  );
}
